import { useState, useCallback, useEffect } from "react";
import { mcpClient } from "../../lib/mcp-client";
import type { HostKind } from "../components/primitives";
import { launchHost, buildBriefingPrompt } from "../lib/host-launchers";
import { getCurrentWorkspaceId } from "../../lib/auth";

interface SessionInfo {
  host_kind: string | null;
  preferred_personas: Record<string, string>;
  last_seen_by_host: Record<string, string>;
  briefing_pending_for_host: Record<string, boolean>;
  last_connect_intent_iso: string | null;
}

type HostState = "never" | "idle" | "active";

export interface UseConnectResult {
  hostStates: Record<string, HostState>;
  lastSeenByHost: Record<string, string>;
  /** Sprint 14f: true when at least one host was seen within the last 24h.
   *  Computed in the load effect (non-render scope) so callers can gate UI on
   *  "user has a live MCP session" without reading the clock during render. */
  hasRecentSession: boolean;
  connectTo: (host: HostKind, agentId: string, agentName?: string) => Promise<void>;
  loading: boolean;
}

const ACTIVE_THRESHOLD_MS = 5 * 60_000;
const RECENT_SESSION_WINDOW_MS = 24 * 60 * 60_000;

function getMru(workspaceId: string): string | null {
  try {
    return localStorage.getItem(`kuvio_connect_mru_${workspaceId}`);
  } catch {
    return null;
  }
}

function setMru(workspaceId: string, host: string): void {
  try {
    localStorage.setItem(`kuvio_connect_mru_${workspaceId}`, host);
  } catch { /* best-effort */ }
}

export function useConnect(): UseConnectResult {
  const [hostStates, setHostStates] = useState<Record<string, HostState>>({});
  const [lastSeenByHost, setLastSeenByHost] = useState<Record<string, string>>({});
  const [hasRecentSession, setHasRecentSession] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const result = await mcpClient.toolsCall<{ session: SessionInfo }>("get_session_info");
        if (cancelled) return;
        const session = result.structuredContent?.session;
        if (!session) return;

        const lastSeen = session.last_seen_by_host ?? {};
        setLastSeenByHost(lastSeen);

        const states: Record<string, HostState> = {};
        const now = Date.now();
        let recent = false;
        for (const [host, iso] of Object.entries(lastSeen)) {
          const elapsed = now - new Date(iso).getTime();
          states[host] = elapsed < ACTIVE_THRESHOLD_MS ? "active" : "idle";
          if (elapsed < RECENT_SESSION_WINDOW_MS) recent = true;
        }
        setHostStates(states);
        setHasRecentSession(recent);
      } catch { /* session may not exist yet */ }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const connectTo = useCallback(async (host: HostKind, agentId: string, agentName?: string) => {
    setLoading(true);
    try {
      await mcpClient.toolsCall("mark_connect_intent", {
        host_kind: host,
        agent_node_id: agentId,
      });

      const workspaceId = getCurrentWorkspaceId();
      if (workspaceId) setMru(workspaceId, host);

      const briefing = agentName ? buildBriefingPrompt(agentName) : undefined;
      const endpoint = (mcpClient as unknown as { config: { endpoint: string } }).config.endpoint;
      launchHost(host, { mcpEndpoint: endpoint, briefing });
    } catch (err) {
      console.error("Connect failed:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  return { hostStates, lastSeenByHost, hasRecentSession, connectTo, loading };
}

export { getMru };
